/**
 * Notification Href Helpers
 *
 * Shared notification destination resolution for inbox, browser delivery,
 * and server-side push payloads.
 *
 * Primary responsibilities:
 * - Resolve direct links from notification data
 * - Build Space item URLs from notification metadata
 *
 * Dependencies:
 * - Space URL helpers
 */

import { buildSpaceItemHref } from '@/lib/spaces/state'
import type {
  SidekickSpaceItemContext,
  SidekickSpaceItemType
} from '@/lib/sidekick/space-item-context'
import type { Notification } from '@/types/notifications'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getStringValue(
  record: Record<string, unknown> | undefined,
  key: string
) {
  const value = record?.[key]
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

function isLegacyNudgeNotification(
  notification: Pick<Notification, 'message' | 'title' | 'type'>,
  data: Record<string, unknown> | undefined
) {
  if (notification.type !== 'reminder' || !getStringValue(data, 'workspaceId')) {
    return false
  }

  const text = `${notification.title} ${notification.message}`.toLowerCase()
  return (
    text.includes('weekly priorities') ||
    text.includes('standup') ||
    text.includes('blocker sweep') ||
    text.includes('nudge')
  )
}

export function resolveNotificationHref(
  notification: Pick<Notification, 'data' | 'message' | 'title' | 'type'>
) {
  const data = isRecord(notification.data) ? notification.data : undefined

  const directHref = getStringValue(data, 'href')
  const actionUrl = getStringValue(data, 'actionUrl')
  const chatId = getStringValue(data, 'chatId')
  const spaceId = getStringValue(data, 'spaceId')
  const conversationId = getStringValue(data, 'conversationId')
  const taskId = getStringValue(data, 'taskId')
  const noteId = getStringValue(data, 'noteId')
  const sprintId = getStringValue(data, 'sprintId')
  const folderId = getStringValue(data, 'folderId')
  const designId = getStringValue(data, 'designId')
  const sheetId = getStringValue(data, 'sheetId')
  const nudgeRunId = getStringValue(data, 'nudgeRunId')
  const nudgeRuleId = getStringValue(data, 'nudgeRuleId')
  const isNudgeNotification =
    Boolean(nudgeRunId || nudgeRuleId) || isLegacyNudgeNotification(notification, data)

  if (isNudgeNotification && chatId) {
    if (directHref) return directHref
    if (actionUrl) return actionUrl

    return `/chat/${chatId}`
  }

  if (spaceId && conversationId) {
    return buildSpaceItemHref(spaceId, {
      type: 'conversation',
      id: conversationId
    })
  }

  if (spaceId && taskId) {
    return buildSpaceItemHref(spaceId, {
      type: 'task',
      id: taskId
    })
  }

  if (spaceId && noteId) {
    return buildSpaceItemHref(spaceId, {
      type: 'note',
      id: noteId
    })
  }

  if (spaceId && sprintId) {
    return buildSpaceItemHref(spaceId, {
      type: 'sprint',
      id: sprintId
    })
  }

  if (spaceId && folderId) {
    return buildSpaceItemHref(spaceId, {
      type: 'folder',
      id: folderId
    })
  }

  if (spaceId && designId) {
    return buildSpaceItemHref(spaceId, {
      type: 'design',
      id: designId
    })
  }

  if (spaceId && sheetId) {
    return buildSpaceItemHref(spaceId, {
      type: 'sheet',
      id: sheetId
    })
  }

  if (spaceId) {
    return `/spaces/${spaceId}`
  }

  if (nudgeRunId) {
    return `/nudges?tab=runs&run=${encodeURIComponent(nudgeRunId)}`
  }

  if (nudgeRuleId || isLegacyNudgeNotification(notification, data)) {
    if (directHref) return directHref
    if (actionUrl) return actionUrl

    return '/nudges?tab=runs'
  }

  if (directHref) {
    return directHref
  }

  if (actionUrl) {
    return actionUrl
  }

  const workspaceId = getStringValue(data, 'workspaceId')
  if (workspaceId) {
    return '/settings?tab=workspace'
  }

  return null
}

export function resolveNotificationSpaceItemContext(
  notification: Pick<Notification, 'data' | 'message' | 'title'>
): SidekickSpaceItemContext | null {
  const data = isRecord(notification.data) ? notification.data : undefined
  const chatId = getStringValue(data, 'chatId')
  const nudgeRunId = getStringValue(data, 'nudgeRunId')
  const nudgeRuleId = getStringValue(data, 'nudgeRuleId')

  if (chatId && (nudgeRunId || nudgeRuleId)) {
    return null
  }

  const spaceId = getStringValue(data, 'spaceId')

  if (!spaceId) {
    return null
  }

  const itemCandidates: Array<{
    id: string | null
    type: SidekickSpaceItemType
  }> = [
    { id: getStringValue(data, 'taskId'), type: 'task' },
    { id: getStringValue(data, 'noteId'), type: 'note' },
    { id: getStringValue(data, 'sprintId'), type: 'sprint' },
    { id: getStringValue(data, 'folderId'), type: 'folder' },
    { id: getStringValue(data, 'conversationId'), type: 'conversation' },
    { id: getStringValue(data, 'designId'), type: 'design' },
    { id: getStringValue(data, 'sheetId'), type: 'sheet' }
  ]
  const item = itemCandidates.find((candidate) => candidate.id)

  if (!item?.id) {
    return null
  }

  return {
    id: item.id,
    spaceId,
    summary: notification.message,
    title:
      getStringValue(data, 'itemTitle') ??
      getStringValue(data, 'title') ??
      notification.title,
    type: item.type
  }
}
